/**
 * SelectionPopover – Shows selected tasks with deselect + fuzzy search to add more
 */

import { useState, useRef, useEffect, useMemo, type RefObject } from 'react';
import { X, Plus, Search } from 'lucide-react';
import { useSelector } from '@xstate/store/react';
import { useTranslation } from 'react-i18next';
import { selectionStore } from '../../stores/selectionStore';
import { useAllTasks } from '../../../stores/hooks';
import { fuzzyFilter, highlightMatches } from '../../utils/fuzzySearch';
import { popoverStyle } from './toolbarStyles';
import type { Task } from '../../../types';

export function SelectionPopover({ onClose, triggerRef }: { onClose: () => void; triggerRef?: RefObject<HTMLElement | null> }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const selectedIds = useSelector(selectionStore, s => s.context.selectedIds);
  const tasks = useAllTasks();

  // Selected tasks
  const selectedTasks = useMemo(() => {
    const idSet = new Set(selectedIds);
    return tasks.filter(t => idSet.has(t.id));
  }, [selectedIds, tasks]);

  // Non-selected tasks for search
  const nonSelectedTasks = useMemo(() => {
    const idSet = new Set(selectedIds);
    return tasks.filter(t => !idSet.has(t.id));
  }, [selectedIds, tasks]);

  // Filtered search results
  const searchResults = useMemo(() => {
    return fuzzyFilter(nonSelectedTasks, searchQuery, (t: Task) => t.title);
  }, [nonSelectedTasks, searchQuery]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (triggerRef?.current?.contains(e.target as Node)) return;
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, triggerRef]);

  const handleDeselect = (taskId: string) => {
    selectionStore.send({ type: 'selection.toggle', taskId });
  };

  const handleAddToSelection = (taskId: string) => {
    selectionStore.send({ type: 'selection.add', taskIds: [taskId] });
  };

  return (
    <div ref={ref} style={{ ...popoverStyle, minWidth: 280, maxWidth: 360 }}>
      {/* Header */}
      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6, fontWeight: 600 }}>
        {t('boardv3.selection.tasksSelected', { count: selectedTasks.length })}
      </div>

      {/* Selected tasks list */}
      <div style={{ maxHeight: 140, overflowY: 'auto', marginBottom: 8 }}>
        {selectedTasks.map((task) => (
          <div
            key={task.id}
            style={{
              fontSize: 11,
              color: '#374151',
              padding: '3px 4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 4,
              borderBottom: '1px solid #f3f4f6',
            }}
          >
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
              {task.title}
            </span>
            <button
              onClick={() => handleDeselect(task.id)}
              style={{
                width: 18,
                height: 18,
                padding: 0,
                border: 'none',
                borderRadius: 3,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'transparent',
                color: '#9ca3af',
                flexShrink: 0,
              }}
              title={t('boardv3.selection.removeFromSelection')}
            >
              <X size={10} />
            </button>
          </div>
        ))}
      </div>

      {/* Divider + Search section */}
      <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 8 }}>
        <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
          {t('boardv3.selection.addToSelection')}
        </div>
        <div style={{ position: 'relative', marginBottom: 4 }}>
          <Search size={10} style={{ position: 'absolute', left: 6, top: 7, color: '#9ca3af' }} />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('boardv3.selection.searchTask')}
            style={{
              width: '100%',
              height: 24,
              fontSize: 11,
              border: '1px solid #e5e7eb',
              borderRadius: 5,
              padding: '0 6px 0 22px',
              outline: 'none',
              boxSizing: 'border-box',
              backgroundColor: '#fff',
              color: '#374151',
            }}
          />
        </div>

        {/* Search results */}
        <div style={{ maxHeight: 160, overflowY: 'auto' }}>
          {searchResults.slice(0, 50).map((task) => {
            const segments = highlightMatches(searchQuery, task.title);
            return (
              <div
                key={task.id}
                style={{
                  fontSize: 11,
                  color: '#374151',
                  padding: '3px 4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 4,
                  borderBottom: '1px solid #f3f4f6',
                  cursor: 'pointer',
                }}
                onClick={() => handleAddToSelection(task.id)}
              >
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                  {segments.map((seg, i) => (
                    seg.highlight
                      ? <mark key={i} style={{ backgroundColor: '#fef08a', borderRadius: 2, padding: '0 1px' }}>{seg.text}</mark>
                      : <span key={i}>{seg.text}</span>
                  ))}
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); handleAddToSelection(task.id); }}
                  style={{
                    width: 18,
                    height: 18,
                    padding: 0,
                    border: 'none',
                    borderRadius: 3,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'transparent',
                    color: '#3b82f6',
                    flexShrink: 0,
                  }}
                  title={t('boardv3.selection.addToSelection')}
                >
                  <Plus size={10} />
                </button>
              </div>
            );
          })}
          {searchQuery && searchResults.length === 0 && (
            <div style={{ fontSize: 11, color: '#9ca3af', padding: '6px 4px', textAlign: 'center' }}>
              {t('boardv3.selection.noResults')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
